// @flow

import React, { type Node, forwardRef } from 'react'
import styled from 'styled-components'
import { TABLET, DESKTOP, REGULAR } from '@streamr/streamr-layout/styled'
import Image from '~shared/Image'
import Link from '~shared/Link'

type Props = {
    author: string,
    className?: string,
    title: string,
    children: Node,
    url: string,
    userImageUrl: string,
    style: Object,
}

const IconWrapper = styled.div`
    svg {
        width: 32px;
        height: 32px;
    }

    @media (min-width: ${TABLET}px) {
        svg {
            width: 48px;
            height: 48px;
        }
    }
`

/* eslint-disable max-len */
const Icon = () => (
    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M24 0c17.736 0 24 6.293 24 24 0 17.64-6.368 24-24 24C6.42 48 0 41.558 0 24 0 6.375 6.316 0 24 0z" fill="#F3F3F3" />
        <g clipPath="url(#clip0)" stroke="#0C009A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M24 34.8V17.387s-2.455-2.727-10.732-2.837a.516.516 0 00-.52.52v16.374a.516.516 0 00.5.52C21.543 32.07 24 34.8 24 34.8zM21 23.855a21.255 21.255 0 00-5.26-1.055M21 28.05A21.313 21.313 0 0015.74 27M27 23.855a21.256 21.256 0 015.26-1.055M27 28.05c1.702-.57 3.47-.923 5.26-1.05" />
            <path d="M32.26 14.67c.76-.066 1.582-.108 2.472-.12a.515.515 0 01.518.52v16.374a.516.516 0 01-.5.52C26.457 32.07 24 34.8 24 34.8M24 34.8V17.387s1.143-1.27 4.5-2.111" />
            <path d="M32.25 19.8v-6.6c-1.264.101-2.518.302-3.75.6v6l1.875-1.5 1.875 1.5z" />
        </g>
        <defs>
            <clipPath id="clip0">
                <path fill="#fff" transform="translate(12 12)" d="M0 0h24v24H0z" />
            </clipPath>
        </defs>
    </svg>
)
/* eslint-enable max-len */

const Headshot = styled.div`
    background: #C4C4C4;
    border-radius: 4px;
    overflow: hidden;
    width: 48px;
    height: 48px;

    img {
        width: 100%;
    }
`

const HeadshotWrapper = styled.div``

const Title = styled.h3`
    font-size: 1rem;
    line-height: 1.5rem;
    font-weight: ${REGULAR};
    margin: 0;

    @media (min-width: ${TABLET}px) {
        font-size: 1.125rem;
        line-height: 2rem;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
    }

    @media (min-width: ${DESKTOP}px) {
        font-size: 1.5rem;
    }
`

const Text = styled.p`
    margin: 0;
    color: #ADADAD;
    font-size: 0.875rem;
    line-height: 1.25rem;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
`

const Author = styled.p`
    margin: 0;
    color: #ADADAD;
    font-size: 0.75rem;
    line-height: 1.25rem;
    text-transform: uppercase;
`

const Content = styled.div`
    overflow: hidden;

    ${Text} {
        display: none;
    }

    @media (min-width: ${TABLET}px) {
        ${Text} {
            display: block;
            margin-top: 0.325rem;
        }
    }
`

const UnstyledPost = forwardRef(({
    className,
    title,
    children,
    author,
    url,
    userImageUrl,
    style,
}: Props, ref: any) => (
    <Link
        href={url}
        className={className}
        newTab
        ref={ref}
        style={style}
        noDecoration
        noHover
    >
        <IconWrapper>
            <Icon />
        </IconWrapper>
        <Content>
            <Title>{title}</Title>
            <Text>{children}</Text>
        </Content>
        <HeadshotWrapper>
            <Headshot>
                <Image
                    src={userImageUrl}
                    alt={author}
                />
            </Headshot>
        </HeadshotWrapper>
        <Author>{author}</Author>
    </Link>
))

const Post = styled(UnstyledPost)`
    position: relative;
    background-color: #FFFFFF;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    padding: 1.5rem 2.5rem 1rem 1.5rem;

    :hover,
    :focus,
    :active,
    :visited {
        text-decoration: none;
    }

    ::after {
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
        border-radius: 5px;
        box-shadow: 0px 0px 20px rgb(0 0 0 / 8%);
        transition: opacity 300ms ease-in-out;
        top: 0;
        left: 0;
        opacity: 0;
    }

    :hover {
        ::after {
            opacity: 1;
        }
    }

    ${Content} {
        margin-top: 14px;
    }

    ${Author} {
        margin-top: 6px;
    }

    ${HeadshotWrapper} {
        display: none;
    }

    @media (min-width: ${TABLET}px) {
        flex-direction: row;
        align-items: center;
        padding: 1.5rem;

        ${HeadshotWrapper} {
            display: block;
        }

        ${Content} {
            margin-top: 0;
            margin-left: 1.5rem;
            margin-right: 3.75rem;
            flex: 1;
        }

        ${Author} {
            display: none;
        }
    }

    @media (min-width: ${DESKTOP}px) {
        padding: 1.25rem 1.5rem;
    }
`

export default Post
